import React from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { Acronym, AcronymsGroupsByLetter } from "@framework/types/glossary"
import Button from "@components/ui/Button/Button"
import { constructComplexUrlWithParams } from "@utils/textUtils"
import Templates from "@components/ui/Templates"
import Text from "@components/ui/Typography/Text"
import useQuery from "@components/hooks/useQuery"
import { useStore } from "@store/index"
import { GlossaryPageMode } from "@pages/upload/Glossary/Glossary"

import AcronymRow from "./AcronymRow"
import Table from "../Table/Table"
import Row from "../Table/Row"
import Column from "../Table/Column"
import AlphabetSwitch from "./AlphabetSwitch"
import EditAcronymRow from "./EditAcronymRow"

import styles from "./index.module.sass"

export interface AcronymTableProps {
  glossaryGroups: AcronymsGroupsByLetter
}

export const AcronymTable: React.FC<AcronymTableProps> = React.memo(
  ({ glossaryGroups }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const queryParams = useQuery()

    const { restrictionsStore: access } = useStore()

    const [letter, setLetter] = React.useState<string>("A")

    const editId = queryParams.get("managingId")

    const handleAdd = () => {
      navigate(
        constructComplexUrlWithParams(location.pathname, {
          glossaryMode: GlossaryPageMode.ACRONYM,
          managingId: "new",
        })
      )
    }

    const items = glossaryGroups?.[letter]

    const editAcronym: Acronym = React.useMemo(() => {
      if (editId !== "new") {
        const toEdit = Object.values(glossaryGroups)
          .flatMap((it) => it)
          .find((it) => it.id === editId)

        if (toEdit != null) return toEdit
      }
      return { id: "new", mainWord: "", definition: "" }
    }, [editId, glossaryGroups])

    return (
      <Templates.RollScript
        className={styles.root}
        gutter="0"
        headerSocket={
          <AlphabetSwitch
            value={letter}
            onChange={setLetter}
            isActive={(it: string) => !!glossaryGroups?.[it]?.length}
            className={styles.switch}
          />
        }
        footerSocket={
          access.canEditGlossary ? (
            editId === "new" ? (
              <Table className={styles.table}>
                <EditAcronymRow acronym={editAcronym} key={editId} />
              </Table>
            ) : (
              <Button
                className={styles.addButton}
                onClick={handleAdd}
                color="primary"
                size="big"
              >
                Add Acronym
              </Button>
            )
          ) : null
        }
      >
        <Table className={styles.table}>
          {items?.length ? (
            items?.map((glossaryUnit) =>
              editId === glossaryUnit.id ? (
                <EditAcronymRow acronym={editAcronym} key={editId} />
              ) : (
                <AcronymRow
                  acronym={glossaryUnit as Acronym}
                  key={`AcronymRow-${glossaryUnit.id}`}
                />
              )
            )
          ) : (
            <Row>
              <Column justify="center">
                <Text color="text50Color">
                  No synonyms added for the letter &quot;{letter}&quot;
                </Text>
              </Column>
            </Row>
          )}
        </Table>
      </Templates.RollScript>
    )
  }
)

export default AcronymTable
